import { clientEnv } from "../env";
import { prepareChatInput } from "./chat-input";
import type { AiChatRequest, AiChatResponse, AiCoreStatus } from "./ai.types";

interface ApiErrorPayload {
  readonly error?: string;
  readonly message?: string;
  readonly text?: string;
}

export type AiVoiceProfile = "feminine" | "masculine";
export type AiVoiceLanguage = "es" | "en";

interface AiSpeechResponse {
  readonly audioBase64: string;
  readonly mimeType: string;
  readonly model: string;
  readonly voice: string;
  readonly disclosure: string;
}

export interface VerifiedSpeechOutput {
  readonly audio: Blob;
  readonly model: string;
  readonly voice: string;
  readonly disclosure: string;
}

interface LegacyAiResult {
  readonly conversationSaved?: boolean;
  readonly requestId?: string;
  readonly status?: string;
  readonly text?: string;
  readonly provider?: string;
  readonly model?: string;
  readonly intent?: {
    readonly primary?: string;
    readonly secondary?: unknown;
    readonly confidence?: number;
  };
  readonly metrics?: {
    readonly provider?: string;
    readonly model?: string;
  };
  readonly usage?: {
    readonly inputTokens?: number;
    readonly outputTokens?: number;
  };
}

export class AiClientError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "AiClientError";
    this.status = status;
    this.code = code;
  }
}

export interface SpeechRequest {
  readonly text: string;
  readonly voiceProfile: AiVoiceProfile;
  readonly voiceLanguage: AiVoiceLanguage;
  readonly sessionToken: string;
  /** Natural-voice tests are allowed to contact self-hosted speech only. */
  readonly localOnly?: boolean;
}

export async function synthesizeAiSpeechDetailed(request: SpeechRequest): Promise<VerifiedSpeechOutput> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/ai/speech`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${request.sessionToken}`
    },
    body: JSON.stringify({
      text: request.text,
      voiceProfile: request.voiceProfile,
      voiceLanguage: request.voiceLanguage,
      ...(request.localOnly === true ? { localOnly: true } : {})
    })
  });

  const payload = await readJson<AiSpeechResponse | ApiErrorPayload>(response);
  if (!response.ok) {
    const errorPayload = payload as ApiErrorPayload;
    throw new AiClientError(
      response.status,
      errorPayload.error ?? "AI_SPEECH_FAILED",
      errorPayload.message ?? "No se pudo generar la voz de OMNISYNIA."
    );
  }

  const speech = payload as AiSpeechResponse;
  if (typeof speech.audioBase64 !== "string" || !speech.audioBase64 ||
      speech.mimeType !== "audio/mpeg" || typeof speech.voice !== "string" ||
      !speech.voice.trim() || typeof speech.model !== "string" || !speech.model.trim()) {
    throw new AiClientError(502, "AI_SPEECH_INVALID", "La respuesta de voz no es válida.");
  }

  let binary: string;
  try {
    binary = atob(speech.audioBase64);
  } catch {
    throw new AiClientError(502, "AI_SPEECH_INVALID", "El audio no contiene base64 válido.");
  }
  if (binary.length < 32 || binary.length > 2_000_000 ||
      !(binary.startsWith("ID3") ||
        (binary.charCodeAt(0) === 0xff && (binary.charCodeAt(1) & 0xe0) === 0xe0))) {
    throw new AiClientError(502, "AI_SPEECH_INVALID", "El audio recibido no tiene un formato MP3 válido.");
  }
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return {
    audio: new Blob([bytes], { type: speech.mimeType }),
    model: speech.model,
    voice: speech.voice,
    disclosure: typeof speech.disclosure === "string" ? speech.disclosure : "Voz sintética."
  };
}

/** Existing client contract stays unchanged for all normal chat and Watch clients. */
export async function synthesizeAiSpeech(request: SpeechRequest): Promise<Blob> {
  return (await synthesizeAiSpeechDetailed(request)).audio;
}

export async function sendAiChat(request: AiChatRequest): Promise<AiChatResponse> {
  const input = prepareChatInput(request.messages);
  if (!input) {
    throw new AiClientError(400, "AI_EMPTY_MESSAGE", "El mensaje está vacío.");
  }

  const response = await fetch(`${clientEnv.apiBaseUrl}/ai/chat`, {
    method: "POST",
    signal: AbortSignal.timeout(90_000),
    headers: {
      "Content-Type": "application/json",
      ...(request.sessionToken ? { Authorization: `Bearer ${request.sessionToken}` } : {})
    },
    body: JSON.stringify({
      message: input.message,
      conversationId: request.conversationId,
      messages: input.messages,
      images: input.images,
      organizationId: request.organizationId,
      metadata: {
        ...request.metadata,
        // A timezone (not GPS coordinates) resolves local news queries such as "hoy".
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        moduleId: request.moduleId
      },
      confirmCredits: true
    })
  });

  const payload = await readJson<LegacyAiResult | ApiErrorPayload>(response);

  if (!response.ok) {
    const errorPayload = payload as ApiErrorPayload;
    throw new AiClientError(
      response.status,
      errorPayload.error ?? "AI_REQUEST_FAILED",
      errorPayload.message ?? errorPayload.text ?? "No se pudo conectar con el motor IA."
    );
  }

  return normalizeLegacyAiResponse(payload as LegacyAiResult, request);
}

export async function getAiCoreStatus(): Promise<AiCoreStatus> {
  const response = await fetch(`${clientEnv.apiBaseUrl}/ai/health`);

  if (!response.ok) {
    throw new AiClientError(response.status, "AI_STATUS_FAILED", "No se pudo leer el estado del motor IA.");
  }

  const payload = await readJson<Record<string, unknown>>(response);

  return {
    status: "ready",
    version: "local-bridge",
    gateway: clientEnv.apiBaseUrl,
    pipeline: ["frontend", "backend", "omnisynia-ai-core", "openai"],
    provider: "openai",
    model: String(payload.model ?? "gpt-4.1-mini"),
    providerAdapters: [],
    memory: "local consent bridge",
    tools: [],
    mediaEngines: ["socialcut-pro"],
    routes: ["socialcut-pro"],
    authentication: "local development",
    session: "anonymous"
  };
}

function normalizeLegacyAiResponse(result: LegacyAiResult, request: AiChatRequest): AiChatResponse {
  const moduleId = request.moduleId ?? "socialcut-pro";
  const provider = result.provider ?? result.metrics?.provider ?? "openai";
  const model = result.model ?? result.metrics?.model ?? "gpt-4.1-mini";
  const content = result.text ?? "OMNISYNIA no devolvió texto.";

  return {
    id: result.requestId ?? crypto.randomUUID(),
    conversationSaved: result.conversationSaved === true,
    provider,
    model,
    route: {
      moduleId,
      capability: "chat",
      reason: "Adaptado desde el AI Core local conectado a OpenAI."
    },
    message: {
      role: "assistant",
      content
    },
    safety: {
      memoryAllowed: request.userConsent.memoryOptIn,
      blocked: false
    },
    session: {
      id: request.sessionId ?? "local-session",
      userMode: "anonymous"
    },
    usage: result.usage,
    core: {
      version: "local-bridge",
      pipeline: ["intent", "context", "model-router", "openai", "response"],
      intent: {
        category: "video",
        targetCapability: "socialcut-pro",
        confidence: result.intent?.confidence ?? 1,
        signals: ["frontend-completo", "backend-8787", "openai-activo"],
        requestedModuleId: moduleId
      },
      context: {
        metadata: request.metadata,
        session: {
          sessionId: request.sessionId ?? "local-session",
          userMode: "anonymous"
        },
        requestedModuleId: moduleId,
        messageWindow: {
          count: request.messages.length,
          hasSystemMessage: request.messages.some((message) => message.role === "system")
        },
        constraints: [],
        safety: {
          memoryAllowed: request.userConsent.memoryOptIn,
          blocked: false
        }
      },
      model: {
        provider: provider === "openai" ? "openai" : "omnisynia-local",
        model,
        adapter: provider,
        reason: "Proveedor detectado desde el backend conectado."
      },
      tools: {
        tools: [],
        mediaEngines: [
          {
            id: "socialcut-pro",
            status: "active",
            moduleId,
            reason: "Módulo activo en la interfaz OMNISYNIA."
          }
        ],
        modules: [moduleId]
      },
      memory: {
        mode: request.userConsent.memoryOptIn ? "consent-eligible" : "ephemeral",
        persistence: "none",
        writable: false,
        reason: request.userConsent.memoryOptIn
          ? "Consentimiento recibido; persistencia pendiente de backend."
          : "Memoria desactivada para esta interacción."
      },
      verification: {
        status: "passed",
        checks: ["backend-response-ok", "frontend-normalized"],
        warnings: []
      }
    },
    createdAt: new Date().toISOString()
  };
}

async function readJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    throw new AiClientError(response.status, "AI_INVALID_RESPONSE", "Respuesta IA no válida.");
  }
}
